import { useCallback } from "react";
import { useLocalization } from "@fluent/react";
import { ContentCard } from "@narmi/design_system";
import {
  getOfferRewardDisplay,
  getOfferExpirationDisplay,
  type RewardOffer,
} from "byzantine/src/dbbl/businessLogic/entities/cardLinkedOffers";

import styles from "./CompactOfferRow.module.scss";

interface CompactOfferRowProps {
  offer: RewardOffer;
  onClick?: (offerId: string) => void;
  removeBorder?: boolean;
  expanded?: boolean;
}

const CompactOfferRow = ({
  offer,
  onClick,
  removeBorder = false,
  expanded = false,
}: CompactOfferRowProps) => {
  const { l10n } = useLocalization();

  // Format reward value based on type
  const displayReward = getOfferRewardDisplay(
    offer.rewardType,
    offer.rewardValue
  );

  // Format expiration date
  const expirationDisplay = offer.expirationDate
    ? getOfferExpirationDisplay(offer.expirationDate)
    : "";

  const handleClick = useCallback(() => {
    onClick?.(offer.id);
  }, [onClick, offer.id]);

  const logoClassName = expanded ? styles.logoExpanded : styles.logo;
  const logoPlaceholderClassName = expanded
    ? styles.logoPlaceholderExpanded
    : styles.logoPlaceholder;

  return (
    <ContentCard paddingSize={"none"}>
      <div
        className={`${styles.offerRow} ${removeBorder ? styles.noBorder : ""}`}
        data-testid={`row-offer-${offer.id}`}
        onClick={handleClick}
      >
        <div className={styles.logoContainer}>
          {offer.logoUrl ? (
            <img
              src={offer.logoUrl}
              alt={offer.name}
              className={logoClassName}
            />
          ) : (
            <div className={logoPlaceholderClassName}>
              {offer.name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className={styles.content}>
          <div className="fontSize--s fontWeight--normal">{offer.name}</div>
          {expanded && expirationDisplay && (
            <div className={styles.expiryText}>{expirationDisplay}</div>
          )}
        </div>
        <div className={styles.reward}>
          <div className="fontSize--s fontWeight--semibold">
            {displayReward}
          </div>
          <div className="fontSize--xs fontWeight--normal">
            {l10n.getString(
              "card-offers-homepage-cash-back",
              null,
              "Cash back"
            )}
          </div>
        </div>
      </div>
    </ContentCard>
  );
};

export default CompactOfferRow;
